import { makeAssistantToolUI, type ToolCallMessagePartProps } from '@assistant-ui/react'

type SetupOption = {
  id: string
  label: string
  detail: string
  enabled: boolean
}

type ChooseSetupPathArgs = { options: SetupOption[] }
type ChooseSetupPathResult = { choice: string }

/**
 * The opening question, rendered as buttons rather than as text to type.
 *
 * This is the whole human-in-the-loop mechanism in one component. The server emits the
 * tool call and deliberately does not answer it, which leaves the call in
 * `requires-action`; this renders the options; `addResult` supplies the answer and the
 * runtime resumes the conversation by sending the transcript back. There is no separate
 * question channel — a question is just a tool nobody has answered yet.
 */
export const ChooseSetupPathToolUI = makeAssistantToolUI<
  ChooseSetupPathArgs,
  ChooseSetupPathResult
>({
  toolName: 'chooseSetupPath',
  render: ChooseSetupPathTool,
})

function ChooseSetupPathTool({
  args,
  status,
  result,
  addResult,
}: ToolCallMessagePartProps<ChooseSetupPathArgs, ChooseSetupPathResult>) {
  const answered = result?.choice
  const waiting = status.type === 'requires-action'

  return (
    <div className="aui-choices">
      {(args.options ?? []).map((option) => {
        const chosen = answered === option.id
        return (
          <button
            key={option.id}
            type="button"
            className={`aui-choice${chosen ? ' is-chosen' : ''}`}
            // Once answered the buttons stay visible but inert: the conversation above
            // should still read as a record of what was decided, not lose its question.
            disabled={!option.enabled || !waiting}
            onClick={() => addResult({ choice: option.id })}
          >
            <span className="aui-choice__label">{option.label}</span>
            <span className="aui-choice__detail">
              {option.enabled ? option.detail : `${option.detail} (not available)`}
            </span>
          </button>
        )
      })}
    </div>
  )
}
